/* app/providers/AuthProvider.jsx — РЕАЛЬНЫЙ AuthContext (боевая сессия Django).
   Контракт значения сохранён: {user, workspaces, activeWorkspace, switchWorkspace, login, logout, refreshUser}
   (+ loading). Bootstrap на маунте: ensureCsrf → /api/auth/me → /api/auth/workspaces.
   Форма данных под потребителей (Sidebar/WorkspaceSwitcher/UserMenu):
   - user.initials — derived из full_name (бэк не присылает);
   - workspace.subscription {plan, planLabel, expires_at, ...} ← бэк {plan_slug, plan_name, ...};
   - workspace.usage — НЕ из /workspaces/ (живёт в /api/subscription/me/); опускаем (UI деградирует на email).
   Пространство вкладки (kitchen-ux A2 круг 2, S10): X-Workspace-Id — своё у каждой вкладки
   (lib/api/client.js), localStorage — общий «последний выбор». adoptWorkspace — перейти в
   пространство, выбранное в соседней вкладке (зовёт WorkspaceTabSync через сторож несохранённого). */

import React from 'react';
import { primeFcScale, setFcScale } from '../../lib/fcScale.js';
import { canUseAssistant, setAssistantAllowed } from '../../lib/assistantAccess.js';
import {
  ensureCsrf, getActiveWorkspaceId, sessionMarkerUser, setActiveWorkspaceId, setTabWorkspaceId, writeSessionMarker,
} from '../../lib/api/client.js';
import * as authApi from '../../lib/api/auth.js';
import { clearServerRegistryCache } from '../../lib/table/serverRegistryCache.js';
import { FullScreenSpinner } from '../../components/ui/Spinner.jsx';
import { stackParams } from '../../hooks/drawerStack.js';
import { analyticsCurrency } from '../../lib/format.js';
import { RR } from '../../lib/router.jsx';
import { isDemoRoute } from '../../screens/demo/demoAccess.jsx';

  const { createContext, useContext, useState, useCallback, useMemo, useEffect, useLayoutEffect, useRef } = React;
  // layout-эффект в браузере; на сервере (SSR-тесты) — обычный, без предупреждения React
  const useCommitEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

  const AuthContext = createContext(null);

  function deriveInitials(fullName, email) {
    const src = (fullName || '').trim();
    if (src) {
      const parts = src.split(/\s+/).filter(Boolean);
      const a = parts[0] ? parts[0][0] : '';
      const b = parts.length > 1 ? parts[parts.length - 1][0] : '';
      return (a + b).toUpperCase();
    }
    return (email || '?').slice(0, 1).toUpperCase();
  }

  function normalizeUser(data) {
    if (!data) return null;
    return { ...data, initials: data.initials || deriveInitials(data.full_name, data.email) };
  }

  // Бэк: subscription {plan_slug, plan_name, is_paid, expires_at, is_effectively_active, expires_soon}|null
  // UI:  subscription {plan, planLabel, expires_at, ...}. membership совпадает (is_owner/is_admin/role_name).
  function normalizeWorkspace(w) {
    const s = w.subscription;
    const subscription = s
      ? {
          plan: s.plan_slug,
          planLabel: s.plan_name,
          expires_at: s.expires_at,
          is_paid: s.is_paid,
          expires_soon: s.expires_soon,
          is_effectively_active: s.is_effectively_active,
        }
      : null;
    // ...w несёт и `sections` ({analytics, integrations} — тарифные флаги разделов): не
    // пересобирать объект по полям, иначе флаги потеряются и разделы «включатся» молча.
    return { ...w, subscription }; // usage опущен (приходит из /api/subscription/me/, см. реестр дыр)
  }

  // Активное пространство = запись списка по activeId (иначе первая, иначе null). Единственный
  // источник, из которого меню/таб-бар/гард /analytics/палитра/уведомления читают тарифные
  // флаги разделов (workspace.sections): смена activeId и обновление списка меняют результат.
  function pickActiveWorkspace(workspaces, activeId) {
    const list = workspaces || [];
    return list.find((w) => w.id === activeId) || list[0] || null;
  }

  /* Кадры drawer-стека (?edit=, ?etab, ?v) принадлежат пространству, где их открыли (kitchen-ux A2
     круг 5, S10 — MAJOR верификатора C4). Вкладка уходит в другое пространство — адрес без них
     (модель hooks/drawerStack.js), записью replace: иначе экран, перемонтированный по новому
     пространству, открыл бы сущность прежнего с чужим заголовком (404 поверх реестра). Уйти уже
     решено (сторож пройден или форма чистая) — переход мимо сторожа (force). → снимали ли. */
  function dropStackFrames(navigate, ws) {
    if (typeof navigate !== 'function' || typeof window === 'undefined' || !window.location) return false;
    const sp = new URLSearchParams(window.location.search || '');
    if (!sp.has('edit')) return false;
    const rest = stackParams(sp, []).toString();
    // запись — уже пространства, куда вкладка уходит (__ws, lib/router.jsx): «назад» сюда — своё
    navigate({ pathname: window.location.pathname, search: rest ? '?' + rest : '' },
      { replace: true, preventScrollReset: true, force: true, ws });
    return true;
  }

  function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [workspaces, setWorkspaces] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [loading, setLoading] = useState(true);

    // Смена пространства вкладки (свой выбор, чужой выбор, список без прежнего): кадры стека
    // прежнего — прочь в том же коммите, что и activeId (см. dropStackFrames). Первое пространство
    // вкладки (загрузка, вход) — не смена: ссылка с ?edit= открывается как есть.
    let navigate = null;
    try { navigate = RR.useNavigate(); } catch { navigate = null; }   // вне роутера — снимать нечего
    const navigateRef = useRef(navigate);
    navigateRef.current = navigate;
    const activeIdRef = useRef(activeId);
    activeIdRef.current = activeId;
    const leaveFramesFor = useCallback((next) => {
      const prev = activeIdRef.current;
      // первое пространство вкладки: ссылка помечается им, запись чужого пространства, восстановленная
      // после перезагрузки, теряет кадры (история — lib/router.jsx, круг 6)
      if (prev == null && next != null) { RR.settleEntryWs(next); return; }
      if (prev != null && next != null && Number(next) !== Number(prev)) dropStackFrames(navigateRef.current, next);
    }, []);

    // Общий выбор (localStorage) пишем, только если: явный вход (persist); у вкладки выбора ещё
    // не было (первый старт браузера); пространство вкладки стало недоступно — сервер вернул
    // другое (архив, отозван доступ), и соседние вкладки должны узнать и перейти через сторож
    // (T8). Своё подтверждённое пространство — только вкладке: фоновое обновление списка и
    // перезагрузка вкладки, оставшейся в своём, не перетягивают соседние (их выбор иначе
    // переключило бы обратно событием storage).
    const loadWorkspaces = useCallback(async (opts) => {
      const persist = !!(opts && opts.persist === true);
      const tabBefore = getActiveWorkspaceId();             // с этим пространством вкладка спросила список
      const r = await authApi.listWorkspaces();
      if (r.ok && r.data) {
        const list = (r.data.workspaces || []).map(normalizeWorkspace);
        setWorkspaces(list);
        const aid = r.data.active_id != null ? r.data.active_id : (list[0] && list[0].id) || null;
        leaveFramesFor(aid);
        setActiveId(aid);
        // → apiFetch шлёт X-Workspace-Id этой вкладки
        if (persist || tabBefore == null || aid !== tabBefore) setActiveWorkspaceId(aid); else setTabWorkspaceId(aid);
        return list;
      }
      return [];
    }, [leaveFramesFor]);
    const workspacesRef = useRef(workspaces);
    workspacesRef.current = workspaces;

    // X-Workspace-Id вкладки следует за activeId в КОММИТЕ его смены — там же, где экраны
    // перемонтируются (RemountOnWorkspace по activeWorkspace): запросы старого экрана до
    // коммита уходят со старым id, эффекты нового экрана — уже с новым.
    // и записи истории роутера помечаются им же (__ws): «назад/вперёд» на запись чужого пространства
    // снимает её кадры стека (lib/router.jsx, круг 6)
    useCommitEffect(() => { if (activeId != null) { setTabWorkspaceId(activeId); RR.setHistoryWs(activeId); } }, [activeId]);

    // Bootstrap сессии: csrf → me → workspaces. Пока в полёте — FullScreenSpinner
    // (иначе RequireAuth редиректнёт на /signin до ответа /me).
    useEffect(() => {
      let alive = true;
      // Публичный демо-стенд (/business-demo) — гость без сессии: не дёргаем csrf/me/workspaces
      // (они всё равно 401 и не нужны на no-login странице).
      if (isDemoRoute()) { setUser(null); setLoading(false); return () => { alive = false; }; }
      (async () => {
        await ensureCsrf();
        const r = await authApi.me();
        if (!alive) return;
        if (r.ok && r.data) {
          setUser(normalizeUser(r.data));
          // загрузились под другим пользователем, чем в маркере сессии (вошли мимо этой вкладки) —
          // переписать маркер: соседние вкладки получат storage и сверят «кто я» (круг 5, S10)
          if (r.data.id != null && sessionMarkerUser() !== String(r.data.id)) writeSessionMarker(r.data.id);
          await loadWorkspaces();
        } else {
          setUser(null); // 401/403 → гость → RequireAuth уведёт на /signin
        }
        if (alive) setLoading(false);
      })();
      return () => { alive = false; };
    }, [loadWorkspaces]);

    const activeWorkspace = useMemo(
      () => pickActiveWorkspace(workspaces, activeId),
      [workspaces, activeId]
    );
    // Валюта пространства (касса не в рублях — AED, KGS…): символ у денег «Продукции» и
    // черновиков чата, без конвертаций. Ставим в рендере, ДО детей — как Аналитика
    // (module-set безопасен: значение — функция активного пространства).
    analyticsCurrency.set((activeWorkspace && activeWorkspace.currency) || 'RUB');
    // Шкала food cost пространства (#502): значение — в рендере, ДО детей (как валюта), а
    // подписчики (FoodCostBadge) узнают о смене после коммита — без обновления чужих
    // компонентов посреди рендера AuthProvider.
    const wsScale = activeWorkspace && activeWorkspace.fc_scale;
    const scaleKey = (activeWorkspace ? activeWorkspace.id : '-') + ':' + JSON.stringify(wsScale || null);
    const primedScaleRef = useRef(null);
    if (primedScaleRef.current !== scaleKey) {
      // только при смене пространства или значения в списке: устаревший список не откатывает
      // шкалу, уже поставленную свежим ответом отчёта или вкладкой настроек
      primedScaleRef.current = scaleKey;
      primeFcScale(wsScale);
    }
    useCommitEffect(() => { setFcScale(wsScale); }, [scaleKey]);  // eslint-disable-line react-hooks/exhaustive-deps
    // №523: право на Аргуса активного пространства — для пунктов «⋮» реестров (вне контекста)
    setAssistantAllowed(canUseAssistant(activeWorkspace));

    // Реальное переключение: сервер-сессия (switch) + localStorage (X-Workspace-Id) — два независимых
    // слоя, оба кормят resolve_active_workspace; держим в синхроне. Затем обновляем флаги/подписку.
    const switchWorkspace = useCallback(async (id) => {
      const r = await authApi.switchWorkspace(id);
      if (r.ok) {
        setActiveWorkspaceId(id);
        leaveFramesFor(id);
        setActiveId(id);
        loadWorkspaces();
      }
      return r;
    }, [loadWorkspaces, leaveFramesFor]);

    // login(userData): принимает payload пользователя (как раньше mock) — экран SignIn сам зовёт
    // authApi.login и передаёт сюда res.data. Поднимаем сессию пространств.
    const login = useCallback(async (userData) => {
      clearServerRegistryCache();   // новая сессия — снимки реестров прежней не показываем (S10)
      if (userData) setUser(normalizeUser(userData));
      // вход — всегда новое значение маркера: соседние вкладки сверят «кто я», даже если общий
      // выбор пространства не изменится (вход в то же 20 → 20 события storage не даёт)
      writeSessionMarker(userData && userData.id != null ? userData.id : '?');
      await loadWorkspaces({ persist: true });
    }, [loadWorkspaces]);

    // Пространство выбрали в СОСЕДНЕЙ вкладке (WorkspaceTabSync, после сторожа несохранённого).
    // Сессию сервера и общий выбор уже переключила та вкладка — здесь только своё состояние;
    // заголовок X-Workspace-Id сменится в коммите перемонтирования (эффект выше). Та вкладка
    // могла создать пространство — тогда сначала свежий список (запрос ещё со своим id).
    // Пространство, выбранное в соседней вкладке, доступно этой? (T9: проверка ДО сторожа —
    // иначе диалог спросил бы про переход, которого не будет.) Незнакомое — свежий список
    // (та вкладка могла его создать; запрос ещё со своим id). → пространство | null.
    // fresh — всегда свежий список (ответ 409 workspace_unavailable: перечитать, но не переключать)
    const findWorkspace = useCallback(async (id, { fresh = false } = {}) => {
      const want = Number(id);
      const known = (workspacesRef.current || []).find((w) => w.id === want);
      if (known && !fresh) return known;
      const r = await authApi.listWorkspaces();
      if (!r.ok || !r.data) return null;
      const list = (r.data.workspaces || []).map(normalizeWorkspace);
      const found = list.find((w) => w.id === want) || null;
      if (found) setWorkspaces(list);
      return found;
    }, []);

    // → true — перешли; false — пространство этой вкладке недоступно (остаёмся; мусорный id
    // отсекается той же проверкой — его нет в списке).
    const adoptWorkspace = useCallback(async (id) => {
      const want = Number(id);
      let list = workspacesRef.current || [];
      if (!list.some((w) => w.id === want)) {
        const r = await authApi.listWorkspaces();
        if (!r.ok || !r.data) return false;
        list = (r.data.workspaces || []).map(normalizeWorkspace);
        if (!list.some((w) => w.id === want)) return false;
        setWorkspaces(list);
      }
      leaveFramesFor(want);
      setActiveId(want);
      return true;
    }, [leaveFramesFor]);

    const logout = useCallback(async () => {
      clearServerRegistryCache();   // снимки реестров вкладки (строки, группы) — не для следующего пользователя (S10)
      await authApi.logout();
      writeSessionMarker(null);
      setUser(null);
      setWorkspaces([]);
      setActiveId(null);
      setActiveWorkspaceId(null);
    }, []);

    const refreshUser = useCallback(async () => {
      const r = await authApi.me();
      if (r.ok && r.data) setUser(normalizeUser(r.data));
      return r;
    }, []);

    const value = useMemo(() => ({
      user, workspaces, activeWorkspace, loading,
      switchWorkspace, adoptWorkspace, findWorkspace, login, logout, refreshUser, refreshWorkspaces: loadWorkspaces,
    }), [user, workspaces, activeWorkspace, loading, switchWorkspace, adoptWorkspace, findWorkspace, login, logout, refreshUser, loadWorkspaces]);

    if (loading) return <FullScreenSpinner label="Загрузка…" />;

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
  }

  const useAuth = () => useContext(AuthContext);

export { AuthProvider };
export { useAuth };
export { normalizeWorkspace, pickActiveWorkspace };
